<template>
    <div class="modal-card">
        <header class="modal-card-head">
            <p class="modal-card-title">Share Project</p>
        </header>
        <b-notification type="is-danger" v-if="errorMessage" :closable="false">
            <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
        </b-notification>
        <section class="modal-card-body">
            <p>
                <b>{{project.name}} </b>
                <span style="font-style:italic;">(Created by {{project.createdBy}} on {{project.created | formatDate}})</span>
            </p>
            <br />
            <b-message type="is-warning" has-icon icon-size="is-small">
                Sharing this project will allow any selected "Shared" user to view the project.
            </b-message>
            <div class="user-selection">
                <div class="available-user-list">
                    <b-field label="Available users">
                        <b-input placeholder="Search..." type="search" icon="search" v-model="availableUserSearchText"></b-input>
                    </b-field>
                    <b-field>
                        <b-select class="select-field"
                        multiple
                        v-model="selectedAvailableUserIds"
                        expanded>
                            <option v-for="user in filteredAvailableUsers"
                                :value="user.id"
                                :key="user.email"
                                @click="doubleClickSelection(shareUsers)"
                                :class="user.isActive ? '' : 'inactive-user'">
                                    {{ user.email }}
                            </option>
                        </b-select>
                    </b-field>
                </div>
                <div class="arrow-buttons">
                    <button type="button" class="button" @click="shareUsers">&gt;</button>
                    <button type="button" class="button" @click="unshareUsers">&lt;</button>
                </div>
                <div class="selected-user-list">
                    <b-field label="Shared users">
                        <b-input placeholder="Search..." type="search" icon="search" v-model="sharedUserSearchText"></b-input>
                    </b-field>
                    <b-field>
                        <b-select class="select-field"
                        multiple
                        v-model="selectedSharedUserIds"
                        expanded>
                            <option v-for="user in filteredSharedUsers"
                            :value="user.id"
                            :key="user.email"
                            @click="doubleClickSelection(unshareUsers)"
                            :class="user.isActive ? '' : 'inactive-user'">
                                {{ user.email }}
                            </option>
                        </b-select>
                    </b-field>
                </div>
            </div>
            <div >
                <b-checkbox size="is-small" v-model="showInActive">
                    Show Inactive Users
                </b-checkbox>
            </div>
            <b-loading :is-full-page="false" :active.sync="isLoading"></b-loading>
        </section>
        <footer class="modal-card-foot">
            <button type="button" class="button" @click="close">Close</button>
            <button type="button" class="button is-primary" @click="save" :disabled="isSaving">
                Apply
                <i v-if="isSaving" class="fas fa-spinner fa-spin is-small"></i>
            </button>
        </footer>
    </div>
</template>

<script>
import userService from '@/services/UserService.js';
import projectShareService from '@/services/project-share-service.js';
import _ from 'lodash';
export default {
    name: 'ProjectShare',
    props: {
        project: {
            type: Object,
            required: false
        }
    },
    data () {
        return {
            errorMessage: '',
            availableUserSearchText: '',
            sharedUserSearchText: '',
            availableUsers: [],
            sharedUsers: [],
            selectedAvailableUserIds: [],
            selectedSharedUserIds: [],
            isLoading: true,
            isSaving: false,
            doubleClickCounter: 0,
            doubleClickTimer: null,
            showInActive: false
        };
    },
    async created () {
        try {
            let results = await Promise.all([
                userService.getUsers(),
                projectShareService.getByProjectId(this.project.id)
            ]);
            let [users, projectShares] = results;
            if (users && users.data.length > 0) {
                if (projectShares && projectShares.data.length > 0) {
                    let sharedUserIds = projectShares.data.map(ps => ps.userId);
                    let partitionedUsers = _.partition(users.data, (u) => {
                        return _.includes(sharedUserIds, u.id);
                    });
                    // First item in the partitionedUser is the shared users.
                    // The second item in the partitionedUser is the remaining available users.
                    this.sharedUsers = _.sortBy(partitionedUsers[0], 'email');
                    this.availableUsers = _.sortBy(partitionedUsers[1], 'email');
                } else {
                    this.availableUsers = _.sortBy(users.data, 'email');
                }
            }
        } catch (error) {
            this.errorMessage = error.message;
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        doubleClickSelection (event) {
            var self = this;
            this.doubleClickCounter++;
            if (this.doubleClickCounter === 1) {
                this.doubleClickTimer = setTimeout(function () {
                    // DO NOTHING BUT RESET IN CASE THERES JUST ONE CLICK
                    self.doubleClickCounter = 0;
                }, 500);
            } else {
                clearTimeout(this.doubleClickTimer);
                event();
                self.doubleClickCounter = 0;
            }
        },
        shareUsers () {
            let partitionedUsers = _.partition(this.availableUsers, (u) => {
                return _.includes(this.selectedAvailableUserIds, u.id);
            });
            // First item in the partitionedUser is the selected users.
            // The second item in the partitionedUser is the remaining available users.
            this.sharedUsers = _(this.sharedUsers).concat(partitionedUsers[0]).sortBy('email').value();
            this.availableUsers = _.sortBy(partitionedUsers[1], 'email');
            this.clearSearchText();
        },
        unshareUsers () {
            let partitionedUsers = _.partition(this.sharedUsers, (u) => {
                return _.includes(this.selectedSharedUserIds, u.id);
            });
            // First item in the partitionedUser is the selected users.
            // The second item in the partitionedUser is the remaining shared users.
            this.availableUsers = _(this.availableUsers).concat(partitionedUsers[0]).sortBy('email').value();
            this.sharedUsers = _.sortBy(partitionedUsers[1], 'email');
            this.clearSearchText();
        },
        clearSearchText () {
            this.availableUserSearchText = '';
            this.sharedUserSearchText = '';
        },
        save () {
            if (this.sharedUsers.length > 0) {
                let projectShareModel = {
                    projectId: this.project.id,
                    userIds: _.map(this.sharedUsers, u => u.id)
                };
                this.isSaving = true;
                projectShareService.shareProject(projectShareModel)
                    .then(response => {
                        let numberOfUsersSharedWith = response.data.length;
                        let message = `Successfully shared project with ${numberOfUsersSharedWith} user${numberOfUsersSharedWith > 1 ? 's' : ''}`;
                        this.$buefy.toast.open({
                            message: message,
                            type: 'is-success'
                        });
                        this.$parent.close();
                    }).catch(
                        error => {
                            this.errorMessage = error.message;
                        }
                    ).finally(() => {
                        this.isSaving = false;
                    });
            } else {
                this.isSaving = true;
                projectShareService.unshareProject(this.project.id)
                    .then(response => {
                        this.$buefy.toast.open({
                            message: 'Successfully unshared project',
                            type: 'is-success'
                        });
                        this.$parent.close();
                    }).catch(
                        error => {
                            this.errorMessage = error.message;
                        }
                    ).finally(() => {
                        this.isSaving = false;
                    });
            }
        },
        close () {
            this.$parent.close();
        }
    },
    computed: {
        filteredAvailableUsers () {
            return this.availableUsers.filter((user) => {
                return user.email.toLowerCase().includes(this.availableUserSearchText.toLowerCase()) &&
                    (user.isActive || this.showInActive);
            });
        },
        filteredSharedUsers () {
            return this.sharedUsers.filter((user) => {
                return user.email.toLowerCase().includes(this.sharedUserSearchText.toLowerCase()) &&
                    (user.isActive || this.showInActive);
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.modal-card-foot {
    justify-content: flex-end;
}
.user-selection {
    display: flex;
    align-items: center;

    .available-user-list,
    .selected-user-list {
        float: left;
        width: 45%;
        ::v-deep select {
            min-height: 300px;
        }
    }

    .arrow-buttons {
        text-align: center;
        float: left;
        width: 10%;
        button {
            margin: 10px;
        }
    }

    .inactive-user {
        font-style: italic;
        color: $text-light;
    }
}
</style>
